import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronsUpDown, Loader2, X } from "lucide-react";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { resolveWorkspaceMemberLabel } from "@/lib/workspace-member-display";
import { normalizeSearch } from "@/lib/normalize-search";
import {
  parseIntegrationAutoAssignMemberIds,
  parseIntegrationSelectedStageSlug,
} from "@/lib/integration-inbound-config";

type StageRow = { slug: string; name: string; order_index: number };
type MemberRow = {
  id: string;
  user_id: string;
  display_name: string | null;
  profile?: { full_name: string | null } | null;
};

interface IntegrationInboundLeadFieldsProps {
  integrationId: string;
  workspaceId: string;
  config: Record<string, unknown> | null | undefined;
  selectedFunnelId: string | null;
  disabled?: boolean;
  onConfigUpdated?: (nextConfig: Record<string, unknown>) => void;
}

export function IntegrationInboundLeadFields({
  integrationId,
  workspaceId,
  config,
  selectedFunnelId,
  disabled,
  onConfigUpdated,
}: IntegrationInboundLeadFieldsProps) {
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const [stages, setStages] = useState<StageRow[]>([]);
  const [loadingStages, setLoadingStages] = useState(false);
  const [members, setMembers] = useState<MemberRow[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [membersOpen, setMembersOpen] = useState(false);
  const [memberSearch, setMemberSearch] = useState("");
  const [localMemberIds, setLocalMemberIds] = useState<string[]>(() =>
    parseIntegrationAutoAssignMemberIds(config),
  );
  const saveChainRef = useRef<Promise<void>>(Promise.resolve());

  const selectedStageSlug = parseIntegrationSelectedStageSlug(config);

  const memberLabel = useCallback((m: MemberRow) => {
    return resolveWorkspaceMemberLabel(m.display_name, m.profile?.full_name ?? null);
  }, []);

  useEffect(() => {
    setLocalMemberIds(parseIntegrationAutoAssignMemberIds(config));
  }, [integrationId, config?.auto_assign_member_ids]);

  const filteredMembers = useMemo(() => {
    if (!memberSearch.trim()) return members;
    const q = normalizeSearch(memberSearch);
    return members.filter((m) => normalizeSearch(memberLabel(m)).includes(q));
  }, [members, memberSearch, memberLabel]);

  const loadWorkspaceMembers = useCallback(async () => {
    if (!workspaceId) return;
    setLoadingMembers(true);
    try {
      const { data: memberRows, error: membersError } = await supabase
        .from("workspace_members")
        .select("id, user_id, display_name")
        .eq("workspace_id", workspaceId)
        .order("display_name");

      if (membersError) throw membersError;

      const rows = memberRows ?? [];
      const userIds = rows.map((m) => m.user_id).filter(Boolean);
      const { data: profiles } =
        userIds.length > 0
          ? await supabase.from("profiles").select("id, full_name").in("id", userIds)
          : { data: [] as { id: string; full_name: string | null }[] };

      const profileMap = new Map((profiles ?? []).map((p) => [p.id, p]));
      setMembers(
        rows.map((m) => ({
          ...m,
          profile: profileMap.get(m.user_id) ?? null,
        })),
      );
    } catch (e) {
      console.error("Erro ao carregar membros do workspace:", e);
      setMembers([]);
    } finally {
      setLoadingMembers(false);
    }
  }, [workspaceId]);

  const memberNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const m of members) map.set(m.id, memberLabel(m));
    return map;
  }, [members, memberLabel]);

  useEffect(() => {
    void loadWorkspaceMembers();
  }, [loadWorkspaceMembers]);

  useEffect(() => {
    if (membersOpen && members.length === 0 && !loadingMembers) {
      void loadWorkspaceMembers();
    }
  }, [membersOpen, members.length, loadingMembers, loadWorkspaceMembers]);

  useEffect(() => {
    if (!workspaceId || !selectedFunnelId) {
      setStages([]);
      return;
    }
    setLoadingStages(true);
    void (async () => {
      const { data } = await supabase
        .from("lead_stages")
        .select("slug, name, order_index")
        .eq("workspace_id", workspaceId)
        .eq("funnel_id", selectedFunnelId)
        .order("order_index");
      setStages(data ?? []);
      setLoadingStages(false);
    })();
  }, [workspaceId, selectedFunnelId]);

  const persistMemberIds = useCallback(
    (memberIds: string[]) => {
      const patch = {
        auto_assign_member_ids: memberIds.length > 0 ? memberIds : null,
      };
      const optimisticConfig = { ...(config ?? {}), ...patch };
      onConfigUpdated?.(optimisticConfig);

      saveChainRef.current = saveChainRef.current
        .then(async () => {
          setSaving(true);
          try {
            const { data: integration, error: fetchErr } = await supabase
              .from("integrations")
              .select("id, config")
              .eq("id", integrationId)
              .single();
            if (fetchErr || !integration) {
              throw fetchErr ?? new Error("Integração não encontrada");
            }

            const current = (integration.config as Record<string, unknown> | null) ?? {};
            const nextConfig = { ...current, ...patch };

            const { error } = await supabase
              .from("integrations")
              .update({ config: nextConfig })
              .eq("id", integrationId);
            if (error) throw error;

            onConfigUpdated?.(nextConfig);
          } catch (e: unknown) {
            const message = e instanceof Error ? e.message : "Não foi possível salvar.";
            toast({
              title: "Erro ao salvar responsáveis",
              description: message,
              variant: "destructive",
            });
            setLocalMemberIds(parseIntegrationAutoAssignMemberIds(config));
          } finally {
            setSaving(false);
          }
        })
        .catch(() => {
          /* evita quebra da cadeia */
        });
    },
    [config, integrationId, onConfigUpdated, toast],
  );

  const persistConfigPatch = async (patch: Record<string, unknown>) => {
    setSaving(true);
    try {
      const { data: integration, error: fetchErr } = await supabase
        .from("integrations")
        .select("id, config")
        .eq("id", integrationId)
        .single();
      if (fetchErr || !integration) throw fetchErr ?? new Error("Integração não encontrada");

      const current = (integration.config as Record<string, unknown> | null) ?? {};
      const nextConfig = { ...current, ...patch };

      const { error } = await supabase
        .from("integrations")
        .update({ config: nextConfig as any })
        .eq("id", integrationId);
      if (error) throw error;

      onConfigUpdated?.(nextConfig);
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : "Não foi possível salvar.";
      toast({
        title: "Erro ao salvar",
        description: message,
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const onStageChange = (value: string) => {
    void persistConfigPatch({
      selected_stage_slug: value === "default" ? null : value,
    });
  };

  const toggleMember = (memberId: string) => {
    setLocalMemberIds((prev) => {
      const next = prev.includes(memberId)
        ? prev.filter((id) => id !== memberId)
        : [...prev, memberId];
      persistMemberIds(next);
      return next;
    });
  };

  const clearMembers = () => {
    setLocalMemberIds([]);
    persistMemberIds([]);
  };

  const funnelExplicit = !!selectedFunnelId;
  const fieldsDisabled = disabled || saving;

  return (
    <>
      <div className="space-y-1.5 sm:col-span-2">
        <Label className="text-xs text-muted-foreground">Coluna de entrada</Label>
        <Select
          value={funnelExplicit ? selectedStageSlug ?? "default" : "default"}
          onValueChange={onStageChange}
          disabled={fieldsDisabled || !funnelExplicit || loadingStages}
        >
          <SelectTrigger className="h-9 w-full text-sm">
            <SelectValue
              placeholder={
                funnelExplicit ? "Coluna padrão (Novo)" : "Selecione um funil acima"
              }
            />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="default">Coluna padrão (Novo)</SelectItem>
            {stages.map((stage) => (
              <SelectItem key={stage.slug} value={stage.slug}>
                {stage.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {!funnelExplicit && (
          <p className="text-[11px] text-muted-foreground">
            Escolha um funil fixo (não &quot;Automático&quot;) para definir a coluna.
          </p>
        )}
      </div>

      <div className="space-y-1.5 sm:col-span-2">
        <Label className="text-xs text-muted-foreground">Responsáveis automáticos</Label>
        <Popover
          open={membersOpen}
          onOpenChange={(open) => {
            setMembersOpen(open);
            if (!open) setMemberSearch("");
          }}
        >
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              role="combobox"
              aria-expanded={membersOpen}
              className="group/member-trigger h-auto min-h-9 w-full justify-between font-normal px-2 py-1.5 hover:bg-primary hover:text-primary-foreground"
              disabled={fieldsDisabled}
            >
              <span className="flex flex-wrap gap-1 text-left flex-1">
                {localMemberIds.length === 0 ? (
                  <span className="text-muted-foreground text-sm group-hover/member-trigger:text-primary-foreground">
                    Selecione os membros responsáveis
                  </span>
                ) : (
                  localMemberIds.map((id) => (
                    <Badge
                      key={id}
                      variant="secondary"
                      className="text-xs font-normal group-hover/member-trigger:bg-primary-foreground/15 group-hover/member-trigger:text-primary-foreground"
                    >
                      {memberNameById.get(id) ?? "Membro"}
                    </Badge>
                  ))
                )}
              </span>
              {saving ? (
                <Loader2 className="ml-2 h-4 w-4 shrink-0 animate-spin opacity-50 group-hover/member-trigger:text-primary-foreground" />
              ) : (
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50 group-hover/member-trigger:text-primary-foreground" />
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="w-[var(--radix-popover-trigger-width)] p-2"
            align="start"
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            <Input
              placeholder="Buscar membro..."
              value={memberSearch}
              onChange={(e) => setMemberSearch(e.target.value)}
              className="h-8 mb-2"
            />
            {loadingMembers ? (
              <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Carregando equipe…
              </div>
            ) : filteredMembers.length === 0 ? (
              <p className="py-4 text-center text-sm text-muted-foreground">
                Nenhum membro encontrado.
              </p>
            ) : (
              <div className="max-h-56 space-y-0.5 overflow-y-auto">
                {filteredMembers.map((member) => {
                  const label = memberLabel(member) || "Membro";
                  const checked = localMemberIds.includes(member.id);
                  return (
                    <label
                      key={member.id}
                      className={cn(
                        "flex cursor-pointer items-center gap-2 rounded-md px-2 py-2 text-sm",
                        "hover:bg-accent hover:text-accent-foreground",
                      )}
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={() => toggleMember(member.id)}
                        disabled={fieldsDisabled}
                        aria-label={label}
                      />
                      <span className="truncate">{label}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </PopoverContent>
        </Popover>
        {localMemberIds.length > 0 && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs text-muted-foreground"
            disabled={fieldsDisabled}
            onClick={clearMembers}
          >
            <X className="h-3 w-3 mr-1" />
            Limpar responsáveis
          </Button>
        )}
      </div>
    </>
  );
}
